import NightlightIcon from "@mui/icons-material/Nightlight";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useCallOffSourceLabel } from "@/hooks/useCallOffSourceLabel";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { INK, STATUS, SURFACE } from "@/theme/tokens";
import { MetaChip } from "@/ui/Badges/MetaChip";
import { resolveCallInTime } from "@/utils/callIns";
import { getPrimaryJobLabel } from "@/utils/jobs";
import { type CalledOffShift, ShiftType } from "@/utils/models";

type Props = {
	callIn: CalledOffShift;
	onClick?: (nurseId: number) => void;
};

/**
 * One call-in on the manager's mobile day list: who called, which shift they gave up, why, and when the
 * call came in.
 *
 * Deliberately built to the same rhythm as `MobileNurseItem` (same padding, name weight, 12px meta row)
 * so switching the segmented control doesn't feel like switching apps — the warning-toned left rule is
 * the only thing that sets it apart, which is exactly the signal the manager is scanning for.
 */
export const MobileCallInItem: FC<Props> = ({ callIn, onClick }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseFormattedName, getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const callOffSourceLabel = useCallOffSourceLabel();

	// Job-management departments organize staff by job, not role, so the card names the employee the way
	// the rest of that department's UI does. `CalledOffShift` carries no job of its own — the shift is
	// gone — so this is the employee's primary job rather than the job they were scheduled under.
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const qualifier = useMemo(() => {
		const nurse = getNurseById(callIn.nurseId);
		return jobManagementEnabled ? getPrimaryJobLabel(nurse) : nurse?.role;
	}, [getNurseById, callIn.nurseId, jobManagementEnabled]);
	const calledAt = resolveCallInTime(callIn);
	const sourceLabel = callOffSourceLabel(callIn.deletedSource);
	const shiftHours = callIn.endTime.diff(callIn.startTime, "hour").hours;

	return (
		<Box
			data-testid={`mobile-call-in-item-${callIn.id}`}
			onClick={onClick ? () => onClick(callIn.nurseId) : undefined}
			role={onClick ? "button" : undefined}
			tabIndex={onClick ? 0 : undefined}
			onKeyDown={
				onClick
					? (event) => {
							if (event.key !== "Enter" && event.key !== " ") return;
							event.preventDefault();
							onClick(callIn.nurseId);
						}
					: undefined
			}
			sx={{
				bgcolor: SURFACE,
				p: 2,
				mb: 1,
				borderLeft: `3px solid ${STATUS.WARNING.main}`,
				cursor: onClick ? "pointer" : "default",
				"&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "-2px" },
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
				<Typography sx={{ fontSize: "14px", fontWeight: 600, color: INK.SECONDARY }}>{getNurseFormattedName(callIn.nurseId)}</Typography>
				{/* The reason is the manager's first question, so it gets the chip rather than the meta row. */}
				<MetaChip
					tone={callIn.message ? "warning" : "neutral"}
					label={callIn.message ?? t("call.in.no.reason.provided")}
					tooltip={callIn.message}
					dataTestId={`mobile-call-in-reason-${callIn.id}`}
				/>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 0.5, color: alpha(INK.SECONDARY, 0.5) }}>
				{callIn.shiftType === ShiftType.Night && <NightlightIcon sx={{ fontSize: 15 }} />}
				{callIn.shiftType === ShiftType.Day && <WbSunnyIcon sx={{ fontSize: 15 }} />}
				<Typography sx={{ fontSize: "12px" }}>
					{callIn.startTime.toFormat("h:mm a")}-{callIn.endTime.toFormat("h:mm a")} {shiftHours}h
				</Typography>
				{qualifier && (
					<>
						<Divider orientation="vertical" sx={{ height: "12px", backgroundColor: alpha(INK.SECONDARY, 0.5) }} />
						<Typography sx={{ fontSize: "12px" }} data-testid={`mobile-call-in-qualifier-${callIn.id}`}>
							{qualifier}
						</Typography>
					</>
				)}
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, color: alpha(INK.SECONDARY, 0.5) }}>
				<ScheduleOutlinedIcon sx={{ fontSize: 14 }} />
				<Typography sx={{ fontSize: "12px" }}>{t("call.in.called.at", { time: calledAt.toFormat("ccc, MMM d, h:mm a") })}</Typography>
				{!!sourceLabel && (
					<>
						<Divider orientation="vertical" sx={{ height: "12px", backgroundColor: alpha(INK.SECONDARY, 0.5) }} />
						<Typography sx={{ fontSize: "12px" }}>{sourceLabel}</Typography>
					</>
				)}
			</Box>
		</Box>
	);
};
